import { Fragment } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { ResponsiveSankey } from "@nivo/sankey"

import { getTheme } from "../../hooks/UseTheme"
import { LabelAuditCard } from "../cards/SimulationAuditCards/LabelAuditCard"
import { ValueBlock } from "../data-display/ValueBlock"
import { KonanEmptyState } from "../utility/KonanEmptyState"
import { ChartsContainer } from "./ChartsContainer"
import { GraphLabelText } from "./GraphLabelText"

export type SankyChartData = {
  nodes: {
    id: string
    uuid: string
    color?: string
    percentage: number
    nodeType:
      | "label"
      | "filter"
      | "script"
      | "scorecardset"
      | "ruleset"
      | "taglist"
      | "program"
      | "calculator"
      | "loop-start"
      | "loop-end"
      | "project"
    name: string
  }[]
  links: { source: string; target: string; value: number; percentage: number }[]
}

export interface SankeyChartProps {
  title: string
  tooltipText?: string
  graphHeight?: number
  range?: number
  isLoading?: boolean
  data: SankyChartData
  chartType?: "config" | "report"
}

/**
 * A Sankey chart component that displays a flow-based visualization for a given input data.
 *
 * @param {SankeyChartProps} props - The props for the SankeyChart component
 * @param {string} title - The title of the chart
 * @param {string} [tooltipText] - Optional tooltip text
 * @param {number} [range] - Optional range value
 * @param {boolean} [isLoading=false] - Whether the chart is in a loading state
 * @param {number} [graphHeight=400] - The height of the graph
 * @param {object} data - The data for the chart
 * @param {object[]} data.nodes - An array of node objects, each with an id, uuid, color, percentage, nodeType, and name
 * @param {object[]} data.links - An array of link objects, each with a source, target, value, and percentage
 * @returns {React.ReactElement} The rendered SankeyChart component
 */
export function SankeyChart(props: Readonly<SankeyChartProps>): React.ReactElement {
  const { t } = useTranslation("graphs")
  const { graphHeight = 400, title, range, tooltipText, isLoading = false, data, chartType = "report" } = props

  const theme = getTheme()

  return (
    <ChartsContainer
      title={title}
      tooltipText={tooltipText}
      range={range}
      isLoading={isLoading}
      graphHeight={graphHeight}
      gutterBottom
    >
      {data.links.length > 0 && data.nodes.length > 0 ? (
        <ResponsiveSankey
          data={data}
          theme={{
            labels: {
              text: {
                fontSize: 14,
                fontWeight: 700,
                fontFamily: '"Inter", sans-serif',
              },
            },
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          colors={(node) => (node as any)?.color}
          align="justify"
          nodeOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={24}
          nodeSpacing={5}
          nodeBorderWidth={0}
          nodeBorderRadius={0}
          nodeTooltip={({ node }) => (
            <Fragment>
              {chartType === "report" ? (
                <Grid container item xs>
                  <LabelAuditCard
                    nodeType={node.nodeType}
                    nodeBody={
                      <Grid item xs={12} container alignItems="flex-start" justifyContent={"space-between"} mt={1}>
                        <ValueBlock value={node.name} size={12} />
                      </Grid>
                    }
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <div className={"chart-tooltip"} style={{ width: "fit-content" }}>
                    <GraphLabelText
                      label={{
                        name: node.name,
                        value: node.value,
                        percent: +node.percentage.toFixed(2),
                      }}
                    />
                  </div>
                </Grid>
              )}
            </Fragment>
          )}
          linkOpacity={0.8}
          linkHoverOthersOpacity={0.25}
          linkContract={4}
          linkBlendMode={"normal"}
          linkTooltip={({ link }) => (
            <Fragment>
              {chartType === "report" ? (
                <Fragment>
                  <Grid item xs={12} mb={1}>
                    <LabelAuditCard
                      nodeType={link.target.nodeType}
                      nodeBody={
                        <Grid item xs={12} container alignItems="flex-start" justifyContent={"space-between"} mt={1}>
                          <ValueBlock value={link.target.name} size={12} />
                        </Grid>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div className={"chart-tooltip"} style={{ width: "fit-content" }}>
                      <GraphLabelText
                        label={{
                          name: link.target.name,
                          value: link.target.value,
                          percent: link.target.percentage,
                        }}
                      />
                    </div>
                  </Grid>
                </Fragment>
              ) : (
                <Grid item xs={12}>
                  <div className={"chart-tooltip"} style={{ width: "fit-content" }}>
                    <GraphLabelText
                      label={{
                        name: t("sankeyChart.tooltip.flowDirection", {
                          sourceName: link.source.name,
                          targetName: link.target.name,
                        }),
                        value: link.value,
                        percent: +link.percentage.toFixed(2),
                      }}
                    />
                  </div>
                </Grid>
              )}
            </Fragment>
          )}
          enableLinkGradient={true}
          labelPosition="inside"
          labelOrientation="vertical"
          labelPadding={-10}
          label={({ value, y0, y1, name }) => {
            // making sure the label is hidden if height of node is less than 100px
            // and formatting the numbers so that 1000 is displayed as 1,000 (for ease of readability)
            // (y1 - y0) / 6 - 12 -> magic numbers deciding if we should display the name based on it's length to avoid text overlapping on the chart
            // TODO:: should look into a pixel length ratio and make these numbers more dynamic,
            // however we will wait for the revamp since inter has different aspect ration that not-sans
            return y1 - y0 > 100 && name.length < (y1 - y0) / 6 - 12
              ? `${name} (${new Intl.NumberFormat("en-IN").format(value)})`
              : ""
          }}
          labelTextColor={theme.palette.base.text[1]}
        />
      ) : (
        <KonanEmptyState title={t("sankeyChart.emptyState.decisionFunnelUnavailable")} />
      )}
    </ChartsContainer>
  )
}
